import React from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

/** Two-line identity cell for dense admin tables: square avatar, name, secondary line. */
export function OpsPersonCell({
  name,
  secondary,
  avatarUrl,
  initial,
  meta,
  className,
}: {
  name: React.ReactNode;
  secondary?: React.ReactNode;
  avatarUrl?: string | null;
  /** Fallback letter(s); defaults to the first character of a string name. */
  initial?: string;
  /** Extra inline element after the secondary line, e.g. a badge. */
  meta?: React.ReactNode;
  className?: string;
}) {
  const fallback =
    initial ?? (typeof name === "string" ? name.trim().charAt(0).toUpperCase() : "?");
  return (
    <div className={cn("flex min-w-0 items-center gap-2.5", className)}>
      <Avatar className="size-7 rounded-full">
        {avatarUrl ? <AvatarImage src={avatarUrl} alt="" /> : null}
        <AvatarFallback className="rounded-full text-[11px]">{fallback || "?"}</AvatarFallback>
      </Avatar>
      <div className="min-w-0">
        <p className="truncate text-sm leading-5 font-medium text-foreground">{name}</p>
        {(secondary || meta) && (
          <div className="flex min-w-0 items-center gap-1.5">
            {secondary && (
              <span className="truncate text-caption leading-4 text-muted-foreground">
                {secondary}
              </span>
            )}
            {meta}
          </div>
        )}
      </div>
    </div>
  );
}

/** Eyebrow-labelled block used inside drawers and detail panels. */
export function OpsDetailSection({
  label,
  children,
  className,
}: {
  label: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={cn("space-y-1.5", className)}>
      <h3 className="eyebrow">{label}</h3>
      {children}
    </section>
  );
}

/** Label / value row for definition-style detail lists. */
export function OpsMetaRow({ label, value }: { label: React.ReactNode; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-2 text-sm">
      <dt className="shrink-0 text-muted-foreground">{label}</dt>
      <dd className="min-w-0 truncate text-right text-foreground">{value}</dd>
    </div>
  );
}
